/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, InputPrice, Select, Loading, Datepicker} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {staffCreate} from '../../../redux/slices/staff.slice'
import {projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  BloodType: state.lookup.BloodType,
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading,
})

const selectStaffData = (state) => ({
  isRedirect: state.staff.isRedirect,
  loading: state.staff.loading,
})

const selectCategoryData = (state) => ({
  category: state.category.category,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {BloodType, projectLookupList, loading} = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectStaffData)
  const {category} = useSelector(selectCategoryData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('BloodType'))
    dispatch(categoryGetAll({SourceTypeId: 2}))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    NameSurname: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectIds: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'REQUIRED.GENERAL'}))
      .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    NameSurname: '',
    BloodTypeId: null,
    Email: null,
    Phone: null,
    Address: null,
    RelativeInformation: null,
    JobStartDate: new Date(),
    LeaveDate: null,
    Salary: 0,
    RegistryNumber: null,
    IdentityNumber: null,
    Title: null,
    Iban: null,
    ProjectIds: [],
    CategoryId: null,
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(staffCreate(values))
      resetForm()
    },
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.STAFF'}),
      path: '/staff/staff',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.STAFF.ADD'})}</PageTitle>
      <KTCard>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.NAMESURNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('NameSurname')}
                />
                {formik.touched.NameSurname && formik.errors.NameSurname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.NameSurname}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'STAFF.CREATE.PROJECTS'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  required
                  data={projectLookupList}
                  {...formik.getFieldProps('ProjectIds')}
                  isMulti
                  closeMenuOnSelect={false}
                  onChange={(item) => {
                    const newData = []
                    item && item.map((data) => newData.push(data.value))
                    formik.setFieldValue('ProjectIds', newData)
                  }}
                />
                {formik.touched.ProjectIds && formik.errors.ProjectIds && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectIds}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.CATEGORY'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={category}
                  {...formik.getFieldProps('CategoryId')}
                  onChange={(item) => formik.setFieldValue('CategoryId', Number(item && item.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.TITLE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Title')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.IDENTITYNUMBER'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  maxLength={11}
                  minLength={11}
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('IdentityNumber')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.REGISTRYNUMBER'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('RegistryNumber')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.PHONE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Phone')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.IBAN'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Iban')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.EMAIL'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  type='email'
                  autoComplete='off'
                  {...formik.getFieldProps('Email')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.ADDRESS'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <textarea
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  rows={4}
                  {...formik.getFieldProps('Address')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.BLOODTYPEID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={BloodType}
                  {...formik.getFieldProps('BloodTypeId')}
                  onChange={(item) => formik.setFieldValue('BloodTypeId', item.value)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.RELATIVEINFORMATION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('RelativeInformation')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.JOBSTARTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('JobStartDate')}
                  onChange={([date]) => formik.setFieldValue('JobStartDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.LEAVEDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('LeaveDate')}
                  onChange={([date]) => formik.setFieldValue('LeaveDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'STAFF.CREATE.SALARY'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('Salary')}
                  onChange={(value) => formik.setFieldValue('Salary', Number(value))}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
